import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserIcon, MailIcon, LockIcon, EyeIcon, EyeOffIcon, ArrowRightIcon, PhoneIcon, CalendarIcon, UsersIcon } from 'lucide-react';
interface RegisterProps {
  navigate: (screen: string, param?: string) => void;
}
export function Register({
  navigate
}: RegisterProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      navigate('otp-verification', 'register'); // Go to OTP verification before KYC
    }, 1500);
  };
  return <div className="flex-1 bg-slate-50 flex flex-col h-full relative overflow-hidden" data-id="element-2606">
      {/* Header Background */}
      <div className="absolute top-0 left-0 right-0 h-56 bg-[#FFD700] rounded-b-[3rem] shadow-lg shadow-[#FFD700]/20" data-id="element-2607" />

      {/* Content */}
      <div className="flex-1 flex flex-col px-6 pt-12 pb-safe z-10 overflow-y-auto" data-id="element-2608">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="flex flex-col items-center mb-10" data-id="element-2609">
          <h1 className="text-3xl font-black text-[#0D3B3B] mb-2 text-center" data-id="element-2610">
            Create Account
          </h1>
          <p className="text-[#0D3B3B]/70 text-sm text-center max-w-[260px] font-medium" data-id="element-2611">
            Join Skydd to start building and protecting your wealth.
          </p>
        </motion.div>

        {/* Register Form */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="bg-white rounded-[2rem] p-6 shadow-xl shadow-emerald-900/5 flex-1 flex flex-col max-w-md w-full mx-auto mb-6" data-id="element-2612">
          <form onSubmit={handleRegister} className="flex-1 flex flex-col" data-id="element-2613">
            <div className="space-y-4 mb-6" data-id="element-2614">
              {/* Name Inputs */}
              <div className="grid grid-cols-2 gap-4" data-id="element-2615">
                <div className="space-y-1.5" data-id="element-2616">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2617">
                    First Name
                  </label>
                  <div className="relative flex items-center" data-id="element-2618">
                    <div className="absolute left-4 text-gray-400" data-id="element-2619">
                      <UserIcon className="w-5 h-5" data-id="element-2620" />
                    </div>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Chinedu" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2621" />
                  </div>
                </div>
                <div className="space-y-1.5" data-id="element-2622">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2623">
                    Last Name
                  </label>
                  <div className="relative flex items-center" data-id="element-2624">
                    <div className="absolute left-4 text-gray-400" data-id="element-2625">
                      <UserIcon className="w-5 h-5" data-id="element-2626" />
                    </div>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Okafor" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2627" />
                  </div>
                </div>
              </div>

              {/* Email Input */}
              <div className="space-y-1.5" data-id="element-2628">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2629">
                  Email Address
                </label>
                <div className="relative flex items-center" data-id="element-2630">
                  <div className="absolute left-4 text-gray-400" data-id="element-2631">
                    <MailIcon className="w-5 h-5" data-id="element-2632" />
                  </div>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2633" />
                </div>
              </div>

              {/* Phone Input */}
              <div className="space-y-1.5" data-id="element-2634">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2635">
                  Phone Number
                </label>
                <div className="relative flex items-center" data-id="element-2636">
                  <div className="absolute left-4 text-gray-400" data-id="element-2637">
                    <PhoneIcon className="w-5 h-5" data-id="element-2638" />
                  </div>
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="0800 000 0000" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2639" />
                </div>
              </div>

              {/* DOB Input */}
              <div className="space-y-1.5" data-id="element-2640">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2641">
                  Date of Birth
                </label>
                <div className="relative flex items-center" data-id="element-2642">
                  <div className="absolute left-4 text-gray-400" data-id="element-2643">
                    <CalendarIcon className="w-5 h-5" data-id="element-2644" />
                  </div>
                  <input type="date" value={dob} onChange={e => setDob(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2645" />
                </div>
              </div>

              {/* Gender Input */}
              <div className="space-y-1.5" data-id="element-2646">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2647">
                  Gender
                </label>
                <div className="relative flex items-center" data-id="element-2648">
                  <div className="absolute left-4 text-gray-400" data-id="element-2649">
                    <UsersIcon className="w-5 h-5" data-id="element-2650" />
                  </div>
                  <select value={gender} onChange={e => setGender(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all appearance-none" required data-id="element-2651">
                    <option value="" disabled data-id="element-2652">
                      Select Gender
                    </option>
                    <option value="male" data-id="element-2653">Male</option>
                    <option value="female" data-id="element-2654">Female</option>
                    <option value="other" data-id="element-2655">Other</option>
                  </select>
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-1.5" data-id="element-2656">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2657">
                  Password
                </label>
                <div className="relative flex items-center" data-id="element-2658">
                  <div className="absolute left-4 text-gray-400" data-id="element-2659">
                    <LockIcon className="w-5 h-5" data-id="element-2660" />
                  </div>
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a secure password" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-12 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2661" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2662">
                    {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-2663" /> : <EyeIcon className="w-5 h-5" data-id="element-2664" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Terms */}
            <div className="mb-6" data-id="element-2665">
              <p className="text-xs text-gray-500 text-center leading-relaxed" data-id="element-2666">
                By creating an account, you agree to our{' '}
                <span className="font-bold text-[#0D3B3B]" data-id="element-2667">
                  Terms of Service
                </span>{' '}
                and{' '}
                <span className="font-bold text-[#0D3B3B]" data-id="element-2668">Privacy Policy</span>
                .
              </p>
            </div>

            {/* Register Button */}
            <button type="submit" disabled={isLoading || !firstName || !lastName || !email || !phone || !dob || !gender || !password} className={`w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all active:scale-95 mt-auto ${isLoading || !firstName || !lastName || !email || !phone || !dob || !gender || !password ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#0D3B3B] text-white hover:bg-[#092D2D] shadow-lg shadow-emerald-900/20'}`} data-id="element-2669">
              {isLoading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2670" /> : <>
                  Create Account
                  <ArrowRightIcon className="w-5 h-5" data-id="element-2671" />
                </>}
            </button>

            {/* Login Link */}
            <div className="mt-6 text-center" data-id="element-2672">
              <p className="text-sm text-gray-500" data-id="element-2673">
                Already have an account?{' '}
                <button type="button" onClick={() => navigate('login')} className="font-bold text-[#2BA89D] hover:text-[#0D3B3B] transition-colors" data-id="element-2674">
                  Log in
                </button>
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>;
}